const PREFIX = '[ALARM]';

/**
 * Creates a function that returns the fallback if its argument is undefined
 *
 * @param {string} fallback - The string to return when value is undefined;
 * @returns {(value: any) => any}
 */
const propertyFallback =
  (fallback) =>
  (value = fallback) =>
    value;

const fb = propertyFallback('unavailable');

/**
 * stringifies the provided object for inclusion in the message
 *
 * { foo: 123, bar: 456, baz: undefined  } => "foo: 123, bar: 456, baz: 'unavailable'"
 *
 * @param {object} context
 * @returns {string}
 */
function stringifyContext(context) {
  return !context
    ? ''
    : Object.entries(context)
        .map(([key, value]) => `${key}: ${fb(value)}`)
        .join(', ');
}

export function stringifyApolloErrors(error) {
  return [error?.networkError, ...(error?.graphQLErrors || [])]
    .filter(Boolean)
    .map(({ message, extensions: { code } = {} }) =>
      code ? `${message} (code: '${code}')` : message
    )
    .join(' | ');
}

/**
 * Creates an alarm logger function which provides some
 * additional formatting and context before passing it through
 * to the provided error logger.
 *
 * @param {Function} errorLogger - function to log the event
 * @param {string} [prefix="[ALARM]"] - optional message prefix
 * @returns {Function}
 */
export function alarm(errorLogger, prefix = PREFIX) {
  return (cause, context, message) => {
    // constructs a message string with prefix, message, original error message, and context
    const msg = [
      PREFIX,
      message,
      cause?.message,
      stringifyContext(context),
      stringifyApolloErrors(cause),
    ]
      .filter(Boolean)
      .join(' | ');

    // pass through to the error logger
    errorLogger(new Error(msg, { cause }), context);
  };
}
